import { Box, Button, Input } from '@mui/material'
import * as React from 'react'
import { GoogleLoginButton, googleUserAtom } from 'infrastructure/googleDrive'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { GDriveSpreadsheet } from '../googleApis/types'
import StandardStateSelector from 'components/utility/StandardStateSelector'
import { SheetView } from './SheetView'
import { SheetStandardsHandler } from './utilities'
import { Standard } from 'shared/types'
import { getStateStandardsAsMap } from 'components/tree/standardUtilities'

export function AddStandardsToCurricula() {
  const [sheetId, setSheetId] = React.useState('')
  const [spreadsheet, setSpreadsheet] = React.useState<GDriveSpreadsheet>()
  const [standardState, setStandardState] = React.useState<string>('')
  const [standardsMap, setStandardsMap] = React.useState<{
    [key: string]: Standard
  }>()

  const [sheetStandardsHandlers, setSheetStandardsHandlers] = React.useState<{
    [title: string]: SheetStandardsHandler | undefined
  }>({})
  const [sheetErrors, setSheetErrors] = React.useState<{
    [title: string]: string[]
  }>({})

  const [addingStandardLists, setAddingStandardLists] = React.useState(false)
  const [addingStandards, setAddingStandards] = React.useState(false)

  const [fetchingSpreadsheet, setFetchingSpreadsheet] = React.useState(false)

  const guser = useAtomValue(googleUserAtom)

  React.useEffect(() => {
    if (guser.credential?.accessToken && sheetId) {
      setFetchingSpreadsheet(true)
      GDriveSpreadsheet.create(guser.credential?.accessToken, sheetId).then(
        (s) => {
          setSpreadsheet(s)
          setFetchingSpreadsheet(false)
        },
      )
    }
  }, [sheetId, guser.credential?.accessToken])

  React.useEffect(() => {
    if (!standardState) return
    getStateStandardsAsMap(standardState).then((sm) => {
      setStandardsMap(sm)
    })
  }, [standardState])

  React.useEffect(() => {
    if (!standardsMap) return
    spreadsheet?.sheets.forEach((sheet) => {
      SheetStandardsHandler.create(sheet, standardsMap)
        .then((ss) => {
          setSheetStandardsHandlers((prev) => {
            return { ...prev, [sheet.properties.title]: ss }
          })
        })
        .catch((e) => {
          setSheetStandardsHandlers((prev) => {
            return { ...prev, [sheet.properties.title]: undefined }
          })
          setSheetErrors((prev) => {
            return { ...prev, [sheet.properties.title]: [e.toString()] }
          })
        })
    })
  }, [spreadsheet, standardsMap])

  if (!guser.credential?.accessToken) {
    return (
      <Box>
        <GoogleLoginButton hideWhenLoggedIn={true} />
      </Box>
    )
  }

  return (
    <Box>
      <Input
        placeholder="Enter Sheet ID"
        onChange={(e) => setSheetId(e.target.value)}
      />
      <StandardStateSelector
        currentSelection={standardState}
        handleChange={setStandardState}
      />
      {fetchingSpreadsheet && <Box>Fetching Spreadsheet...</Box>}
      {standardState && spreadsheet && (
        <Box>
          <Box>
            <Button
              disabled={addingStandards || addingStandardLists}
              onClick={() => {
                setAddingStandardLists(true)
                Promise.all(
                  Object.values(sheetStandardsHandlers).map((ssh) =>
                    ssh?.addStandardListsToLessons(),
                  ),
                ).then(() => {
                  setAddingStandardLists(false)
                })
              }}
            >
              {addingStandardLists
                ? 'Adding All Standard Lists to All Valid Trees...'
                : 'Add All Standard Lists to All Valid Trees'}
            </Button>
          </Box>
          <Box>
            <Button
              disabled={addingStandards || addingStandardLists}
              onClick={() => {
                setAddingStandards(true)
                Promise.all(
                  Object.values(sheetStandardsHandlers).map((ssh) =>
                    ssh?.addStandards(),
                  ),
                ).then(() => {
                  setAddingStandards(false)
                })
              }}
            >
              {addingStandards
                ? 'Adding All Standards To All Valid Trees...'
                : 'Add All Standards To All Valid Trees'}
            </Button>
          </Box>
          {spreadsheet.sheets.map((s) => {
            return (
              <SheetView
                key={s.properties.sheetId}
                title={s.properties.title}
                sheetStandardsHandler={
                  sheetStandardsHandlers[s.properties.title]
                }
                errors={sheetErrors[s.properties.title]}
              />
            )
          })}
        </Box>
      )}
    </Box>
  )
}
